import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Sparkles, Loader2, Signpost, Palette, Database } from "lucide-react";

const CAT_META = {
  divieti: { label: "Divieti", cls: "bg-rose-50 border-rose-300 text-rose-900", accent: "bg-rose-600 text-white" },
  obblighi: { label: "Obblighi", cls: "bg-sky-50 border-sky-300 text-sky-900", accent: "bg-sky-600 text-white" },
  avvertimenti: { label: "Avvertimenti", cls: "bg-amber-50 border-amber-300 text-amber-900", accent: "bg-amber-500 text-white" },
  emergenza_salvataggio: { label: "Emergenza/Salvataggio", cls: "bg-emerald-50 border-emerald-300 text-emerald-900", accent: "bg-emerald-600 text-white" },
  antincendio: { label: "Antincendio", cls: "bg-rose-50 border-rose-300 text-rose-900", accent: "bg-red-700 text-white" },
};

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Backend-proxied UNI EN ISO 7010 pictogram URL. Caches locally on the server,
// avoiding CORS issues and Wikimedia rate limits. Returns a grey placeholder
// SVG with the code baked-in if upstream is unreachable.
function iso7010Url(code) {
  if (!code) return null;
  const c = String(code).toUpperCase();
  return `${BACKEND}/api/iso7010-image/${c}?fmt=svg`;
}

export default function StepSegnaletica({ dvr, data, onChange, aiLoading, setAiLoading }) {
  const d = data || {};
  const s1 = dvr.steps?.step1_dati_generali || {};
  const [note, setNote] = useState(d._note || "");

  const run = async () => {
    setAiLoading(true);
    try {
      const attrezzature = (dvr.steps?.attrezzature || []).map((a) => a.riconoscimento?.tipo_macchina).filter(Boolean);
      const sostanze = (dvr.steps?.sostanze || []).map((s) => s.prodotto?.nome).filter(Boolean);
      const r = await api.post("/dvr/ai/segnaletica", {
        ateco: s1.ateco,
        tipo_attivita: s1.tipo_attivita,
        superficie_mq: s1.superficie_mq,
        piani: s1.piani,
        attrezzature_presenti: attrezzature,
        sostanze_presenti: sostanze,
        note,
      }, { timeout: 120000 });
      onChange({ ...r.data, _note: note });
      toast.success(`Segnaletica generata (${["divieti","obblighi","avvertimenti","emergenza_salvataggio","antincendio"].reduce((a,k)=>a+(r.data[k]?.length||0),0)} cartelli)`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "AI error");
    }
    setAiLoading(false);
  };

  const totale = ["divieti", "obblighi", "avvertimenti", "emergenza_salvataggio", "antincendio"]
    .reduce((a, k) => a + (d[k]?.length || 0), 0);

  return (
    <div className="space-y-4" data-testid="step-segnaletica">
      <p className="text-sm text-muted-foreground">
        Generazione della segnaletica di sicurezza ex <b>D.Lgs. 81/08 Titolo V + UNI EN ISO 7010</b>.
        L'AI incrocia attività, attrezzature e sostanze e restituisce l'elenco cartelli con codici
        standardizzati e pittogrammi ufficiali.
      </p>
      <Textarea
        rows={2}
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="Note (aree particolari, esposizioni specifiche, cartelli già presenti…)"
        data-testid="seg-note"
      />
      <Button onClick={run} disabled={aiLoading} className="rounded-full" data-testid="seg-run-ai">
        {aiLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Signpost className="w-4 h-4 mr-2" />}
        Genera segnaletica
      </Button>

      {totale > 0 && (
        <div className="text-sm text-muted-foreground">
          <b>{totale}</b> cartelli identificati
        </div>
      )}

      {["divieti", "obblighi", "avvertimenti", "emergenza_salvataggio", "antincendio"].map((cat) => {
        const arr = d[cat] || [];
        if (!arr.length) return null;
        const meta = CAT_META[cat];
        return (
          <div key={cat} className={`border rounded-md ${meta.cls}`}>
            <div className={`px-3 py-1.5 text-xs font-semibold uppercase tracking-widest ${meta.accent} rounded-t-md`}>
              {meta.label} ({arr.length})
            </div>
            <div className="p-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {arr.map((c, i) => (
                <div key={`${cat}-${c.codice || i}`} className="flex items-start gap-3 bg-white/60 rounded-md p-2 border border-border/60">
                  <img
                    src={iso7010Url(c.codice)}
                    alt={c.codice}
                    className="w-14 h-14 object-contain shrink-0 border border-border rounded bg-white"
                    onError={(e) => { e.target.style.display = "none"; }}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="font-mono text-[11px] font-semibold">{c.codice}</div>
                    <div className="text-sm font-semibold">{c.nome}</div>
                    {c.dove && <div className="text-xs mt-0.5">📍 {c.dove}</div>}
                    {c.dimensione_min_cm && <div className="text-[11px] text-muted-foreground">Dim. min: {c.dimensione_min_cm}</div>}
                    {c.descrizione && <div className="text-[11px] mt-0.5 italic text-muted-foreground">{c.descrizione}</div>}
                    {c.normativa && <div className="text-[10px] text-muted-foreground mt-0.5">Rif: {c.normativa}</div>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {d.colori_utilizzati?.length > 0 && (
        <div className="border border-border rounded-md p-3">
          <div className="flex items-center gap-1 font-semibold text-sm mb-2">
            <Palette className="w-4 h-4" /> Codici colore (UNI EN ISO 7010)
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
            {d.colori_utilizzati.map((c, i) => (
              <div key={`col-${c.colore || i}`} className="border border-border rounded p-2">
                <div className="font-semibold">{c.colore}</div>
                <div className="text-muted-foreground">{c.significato}</div>
                {c.ral && <div className="text-[10px]">RAL: {c.ral}</div>}
              </div>
            ))}
          </div>
        </div>
      )}

      {d.note_manutenzione?.length > 0 && (
        <div className="text-sm">
          <b>Manutenzione:</b>
          <ul className="mt-1 space-y-0.5">
            {d.note_manutenzione.map((n, i) => <li key={`note-${i}-${(n || "").slice(0, 20)}`}>• {n}</li>)}
          </ul>
        </div>
      )}
      {d.sintesi && <div className="text-sm text-muted-foreground italic">{d.sintesi}</div>}
    </div>
  );
}
